// Compact numeric pager — used across public list pages and admin.
// Server should return { items, total, page, page_size }.
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

function pagesArray(current, totalPages) {
  const span = 1; // pages either side of current
  const pages = new Set([1, totalPages, current, current - 1, current + 1, current - span, current + span]);
  const cleaned = Array.from(pages).filter((p) => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const withDots = [];
  for (let i = 0; i < cleaned.length; i++) {
    withDots.push(cleaned[i]);
    if (i < cleaned.length - 1 && cleaned[i + 1] - cleaned[i] > 1) withDots.push("…");
  }
  return withDots;
}

export default function Pager({ page, pageSize, total, onChange, className = "", testIdPrefix = "pager" }) {
  const totalPages = Math.max(1, Math.ceil((total || 0) / (pageSize || 1)));
  if (totalPages <= 1) return null;

  const go = (p) => {
    const next = Math.min(totalPages, Math.max(1, p));
    if (next !== page) {
      onChange(next);
      // scroll to top of the results section for a comfortable UX
      if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const pages = pagesArray(page, totalPages);
  const from = (page - 1) * pageSize + 1;
  const to = Math.min(total, page * pageSize);

  return (
    <nav
      aria-label="Pagination"
      className={`mt-8 flex flex-col items-center justify-between gap-4 border-t border-slate-200 pt-6 sm:flex-row ${className}`}
      data-testid={testIdPrefix}
    >
      <div className="text-xs text-slate-500">
        Showing <span className="font-semibold text-slate-700">{from.toLocaleString()}</span>–<span className="font-semibold text-slate-700">{to.toLocaleString()}</span> of <span className="font-semibold text-slate-700">{total.toLocaleString()}</span>
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1"
          disabled={page <= 1}
          onClick={() => go(page - 1)}
          data-testid={`${testIdPrefix}-prev`}
        >
          <ChevronLeft className="h-3.5 w-3.5" /> Prev
        </Button>
        {pages.map((p, i) =>
          p === "…" ? (
            <span key={`e-${i}`} className="px-2 text-slate-400">…</span>
          ) : (
            <Button
              key={p}
              variant={p === page ? "default" : "outline"}
              size="sm"
              className={`h-8 min-w-8 px-2 ${p === page ? "bg-primary text-primary-foreground" : ""}`}
              onClick={() => go(p)}
              data-testid={`${testIdPrefix}-page-${p}`}
            >
              {p}
            </Button>
          )
        )}
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1"
          disabled={page >= totalPages}
          onClick={() => go(page + 1)}
          data-testid={`${testIdPrefix}-next`}
        >
          Next <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    </nav>
  );
}
